import React, { createContext, useContext, useMemo, useState } from "react";

import { useFiltersContext } from "./Filters";

import {
    EjectionReportsContextProps,
    EjectionReportsContextValues,
} from "../types/contexts";
import { SortColumns } from "../types/games";

import { defaultDateFormat } from "../../../../lib/utils";
import { IEjectionReport } from "../../../../types/api";
import { SortOrders } from "../../../../types/shared";

const EjectionReportsContext =
    createContext<EjectionReportsContextValues | null>(null);

export const useEjectionReportsContext = () => {
    const context = useContext(EjectionReportsContext);
    if (!context) {
        throw new Error(
            "useEjectionReportsContext must be used within a EjectionReportsProvider",
        );
    }
    return context;
};

export const EjectionReportsProvider: React.FC<
    EjectionReportsContextProps
> = props => {
    const { children, initialReports } = props;
    const { filters } = useFiltersContext();
    const [reports, setReports] = useState<IEjectionReport[]>(initialReports);
    const [sortColumn, setSortColumn] = useState<SortColumns>("date");
    const [sortOrder, setSortOrder] = useState<SortOrders>("ascending");

    const filteredReports = useMemo(() => {
        return reports
            .filter(r => {
                return filters.dateStart
                    ? new Date(defaultDateFormat.format(new Date(r.date))) >=
                          new Date(defaultDateFormat.format(filters.dateStart))
                    : true;
            })
            .filter(r => {
                return filters.dateEnd
                    ? new Date(defaultDateFormat.format(new Date(r.date))) <=
                          new Date(defaultDateFormat.format(filters.dateEnd))
                    : true;
            })
            .filter(r =>
                filters.umpire.filter
                    ? filters.umpire.filter?.includes(r.userId)
                    : true,
            );
    }, [reports, filters]);

    const sortedReports = useMemo(() => {
        let aValue: any;
        let bValue: any;

        return [...filteredReports].sort((a, b) => {
            switch (sortColumn) {
                case "site":
                    aValue = a.site;
                    bValue = b.site;
                    break;
                case "umpire":
                    aValue = a.umpires?.find(u => u.id === a.userId)?.name;
                    bValue = b.umpires?.find(u => u.id === b.userId)?.name;
                    break;
                case "date":
                    aValue = new Date(a.date).getTime();
                    bValue = new Date(b.date).getTime();
                    break;
            }

            if (sortOrder === "ascending") {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    }, [filteredReports, sortColumn, sortOrder]);

    const contextValue = useMemo(
        () => ({
            reports: sortedReports,
            setReports,
            totalItems: sortedReports.length,
            sortColumn,
            setSortColumn,
            sortOrder,
            setSortOrder,
        }),
        [sortedReports],
    );

    return (
        <EjectionReportsContext.Provider value={contextValue}>
            {children}
        </EjectionReportsContext.Provider>
    );
};
